import { createRouter, createWebHistory, isNavigationFailure } from 'vue-router';
import { LOCAL_STORAGE_KEY, PATIENT_ONBOARDING_STATUS } from '@/helpers/enums';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import OnboardingSessionManager from '@/helpers/onboarding-session-manager';
import routes from './routes';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Navigation guard to check authentication
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const onboardingStatus = OnboardingSessionManager.getOnboardingStatus();

  // Routes that do NOT require authentication → allow access
  if (!to.meta.requiresAuth) {
    return true;
  }

  if (!authStore.isAuthReady) {
    await authStore.initAuth();
  }

  // If NOT logged in
  if (!authStore.isLoggedIn) {
    if (to.path.startsWith('/auth/authorize/') || to.path.startsWith('/auth/patient-selection')) return true;
    const patientMainUnique = localStorage.getItem(LOCAL_STORAGE_KEY.PATIENT_MAIN_UNIQUE);

    // Redirect to authorize if pmu exists, otherwise go to patient selection
    if (patientMainUnique) {
      return { path: `/auth/authorize/${patientMainUnique}` };
    }
    return avoidSelf(to, { path: '/auth/patient-selection' });
  }

  // Already logged in
  const allowedStatuses = to.meta.allowedOnboardingStatuses;

  // Check if current onboarding status is allowed for this route
  if (!allowedStatuses || allowedStatuses.includes(onboardingStatus)) {
    return true;
  }

  // If not allowed, redirect to appropriate page based on status
  if (onboardingStatus === PATIENT_ONBOARDING_STATUS.NOT_YET) {
    return avoidSelf(to, { path: '/onboarding' });
  }
  return avoidSelf(to, { path: '/savanne' });
});

router.afterEach(() => {
  // Reset topbar state to default after each navigation
  const appStore = useAppStore();
  appStore.setTopbarNavigationTitle('');
  appStore.setTopbarProcessTitle('');
  appStore.setTopbarNavigationTimeLabel('');
  appStore.setTopbarShowCloseButton(false);
  appStore.setTopbarHideLeft(false);
  appStore.setOverlayTopbar(false);
});

router.onError((err) => console.error('[router.onError]', err));
router.afterEach((to, from, failure) => {
  if (failure && isNavigationFailure(failure)) {
    console.error('[router.afterEach failure]', failure, { to, from });
  }
});

const avoidSelf = (to, target) => {
  const resolved = typeof target === 'string' ? target : target.path;
  return to.path === resolved ? true : target;
};

export default router;
